import { db, SYNCED_TABLES, syncedTable, type SyncedTableName } from './db';
import { nowMs } from './clock';
import { DEFAULT_COURSE } from '@/constants/app';
import type {
  Item,
  LocalSettings,
  Session,
  SyncedPrefs,
  SyncedRecord,
} from '@/domain/types';

/**
 * The only place records are written. Everything routed through here gets a
 * fresh `updatedAt`, which is what keeps sync correct — no feature code sets
 * that field itself.
 */
export async function put<T extends SyncedRecord>(
  table: SyncedTableName,
  record: Omit<T, 'updatedAt'> & { updatedAt?: number },
): Promise<T> {
  const stamped = { ...record, updatedAt: nowMs() } as T;
  await syncedTable(table).put(stamped as never);
  return stamped;
}

export async function putMany<T extends SyncedRecord>(
  table: SyncedTableName,
  records: Array<Omit<T, 'updatedAt'> & { updatedAt?: number }>,
): Promise<T[]> {
  const stamped = records.map((r) => ({ ...r, updatedAt: nowMs() }) as T);
  if (stamped.length) await syncedTable(table).bulkPut(stamped as never[]);
  return stamped;
}

/** Tombstone, never a physical delete — sync has to be able to see the removal. */
export async function tombstone(table: SyncedTableName, id: string): Promise<void> {
  const existing = await syncedTable(table).get(id);
  if (!existing) return;
  await syncedTable(table).put({ ...existing, deleted: true, updatedAt: nowMs() } as never);
}

export async function tombstoneMany(table: SyncedTableName, ids: string[]): Promise<void> {
  if (!ids.length) return;
  const rows = await syncedTable(table).bulkGet(ids);
  const updates = rows
    .filter((r): r is { id: string; updatedAt: number } => Boolean(r))
    .map((r) => ({ ...r, deleted: true, updatedAt: nowMs() }));
  if (updates.length) await syncedTable(table).bulkPut(updates as never[]);
}

/** Applies a record that arrived from the server, keeping the server's timestamp. */
export async function applyRemote(
  table: SyncedTableName,
  record: SyncedRecord & Record<string, unknown>,
): Promise<void> {
  await syncedTable(table).put(record as never);
}

const alive = <T extends SyncedRecord>(rows: T[]): T[] => rows.filter((r) => !r.deleted);

export async function listSessions(): Promise<Session[]> {
  const rows = alive(await db.sessions.toArray());
  return rows.sort(sessionOrder);
}

export function sessionOrder(a: Session, b: Session): number {
  if (a.kind !== b.kind) return a.kind === 'class' ? -1 : 1;
  const an = numberFromSession(a);
  const bn = numberFromSession(b);
  if (an !== bn) return an - bn;
  return a.title.localeCompare(b.title);
}

function numberFromSession(s: Session): number {
  if (typeof s.number === 'number') return s.number;
  const m = /(\d+)/.exec(s.id);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

export async function getSession(id: string): Promise<Session | undefined> {
  const s = await db.sessions.get(id);
  return s && !s.deleted ? s : undefined;
}

export async function listItems(sessionId?: string): Promise<Item[]> {
  const rows = sessionId
    ? await db.items.where('sessionId').equals(sessionId).toArray()
    : await db.items.toArray();
  return alive(rows).sort((a, b) => a.order - b.order);
}

export async function listAllItems(): Promise<Item[]> {
  return alive(await db.items.toArray());
}

/** Tombstones a session and everything that belongs to it. */
export async function deleteSession(sessionId: string): Promise<void> {
  const [items, templates, formulas, constants] = await Promise.all([
    db.items.where('sessionId').equals(sessionId).primaryKeys(),
    db.templates.where('sessionId').equals(sessionId).primaryKeys(),
    db.formulas.where('sessionId').equals(sessionId).primaryKeys(),
    db.constants.where('sessionId').equals(sessionId).primaryKeys(),
  ]);
  await tombstoneMany('items', items as string[]);
  await tombstoneMany('templates', templates as string[]);
  await tombstoneMany('formulas', formulas as string[]);
  await tombstoneMany('constants', constants as string[]);
  await tombstone('sessions', sessionId);
}

// ---------------------------------------------------------------- local state

function makeDeviceId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const DEFAULT_LOCAL: Omit<LocalSettings, 'deviceId'> = {
  id: 'local',
  theme: 'system',
  difficultyOverride: 'auto',
  allowLibraryInExams: false,
  focusLatestClass: false,
  includeTextbookInCumulative: false,
  blindMultipleChoice: true,
  panelsDocked: true,
  dockSize: 0.45,
  openPanel: null,
  cumulativeCardCount: 40,
  cumulativeQuizCount: 20,
  examPresets: [],
  syncCursor: '',
  lastPushedAt: 0,
  lastSyncAt: null,
  token: null,
  tokenExpiresAt: null,
  /**
   * Empty means same origin, which is right in a browser. The native build
   * runs from https://localhost, where "same origin" is the phone and there is
   * no server at all, so that build sets VITE_SYNC_ORIGIN and this picks it up.
   * Only ever a default: Settings still wins, and an install that already has
   * a value keeps it.
   */
  serverUrl: import.meta.env.VITE_SYNC_ORIGIN ?? '',
  persistentStorage: 'unknown',
};

export async function getLocal(): Promise<LocalSettings> {
  const existing = await db.local.get('local');
  if (!existing) {
    const fresh: LocalSettings = { ...DEFAULT_LOCAL, deviceId: makeDeviceId() };
    await db.local.put(fresh);
    return fresh;
  }

  // A record written by an older build can be missing fields added since.
  // Backfilling here keeps every reader from having to guard for undefined.
  const filled: LocalSettings = { ...DEFAULT_LOCAL, ...existing, id: 'local' };
  const defaults = DEFAULT_LOCAL as unknown as Record<string, unknown>;
  const target = filled as unknown as Record<string, unknown>;
  for (const key of Object.keys(defaults)) {
    if (target[key] === undefined) target[key] = defaults[key];
  }
  // These two were about the whiteboard alone before every panel could dock.
  const older = existing as unknown as { whiteboardDocked?: boolean; whiteboardSize?: number };
  if (typeof older.whiteboardSize === 'number') filled.dockSize = older.whiteboardSize;
  if (typeof older.whiteboardDocked === 'boolean' && !older.whiteboardDocked) {
    // Someone who had unpinned the board had said something; keep it said.
    filled.panelsDocked = false;
  }

  if (typeof filled.syncCursor !== 'string') filled.syncCursor = '';
  if (typeof filled.lastPushedAt !== 'number') filled.lastPushedAt = 0;
  if (!filled.deviceId) filled.deviceId = makeDeviceId();

  if (JSON.stringify(filled) !== JSON.stringify(existing)) await db.local.put(filled);
  return filled;
}

export async function setLocal(patch: Partial<LocalSettings>): Promise<LocalSettings> {
  const current = await getLocal();
  const next = { ...current, ...patch, id: 'local' as const };
  await db.local.put(next);
  return next;
}

export async function getPrefs(): Promise<SyncedPrefs> {
  const existing = await db.prefs.get('prefs');
  if (existing) return existing;
  const fresh: SyncedPrefs = {
    id: 'prefs',
    updatedAt: nowMs(),
    deleted: false,
    sigfigMode: 'lenient',
  };
  await db.prefs.put(fresh);
  return fresh;
}

export async function setPrefs(patch: Partial<Omit<SyncedPrefs, 'id'>>): Promise<SyncedPrefs> {
  const current = await getPrefs();
  const next: SyncedPrefs = { ...current, ...patch, id: 'prefs', updatedAt: nowMs() };
  await db.prefs.put(next);
  return next;
}

// -------------------------------------------------------------------- backup

export interface BackupFile {
  format: 'chem-bank-backup';
  version: 1;
  exportedAt: string;
  course: string;
  tables: Record<string, unknown[]>;
}

export async function exportBackup(): Promise<BackupFile> {
  const tables: Record<string, unknown[]> = {};
  for (const name of SYNCED_TABLES) {
    tables[name] = await syncedTable(name).toArray();
  }
  return {
    format: 'chem-bank-backup',
    version: 1,
    exportedAt: new Date().toISOString(),
    course: DEFAULT_COURSE,
    tables,
  };
}

export interface BackupImportResult {
  restored: number;
  skipped: number;
  perTable: Record<string, number>;
}

/**
 * Restores a backup record by record, keeping whichever copy has the newer
 * `updatedAt`. That makes restoring safe on a device that already has data.
 */
export async function importBackup(file: unknown): Promise<BackupImportResult> {
  const parsed = file as BackupFile;
  if (!parsed || parsed.format !== 'chem-bank-backup') {
    throw new Error('This file is not a Chem Bank backup.');
  }
  if (parsed.version !== 1) {
    throw new Error(`Backup version ${String(parsed.version)} is not supported.`);
  }

  let restored = 0;
  let skipped = 0;
  const perTable: Record<string, number> = {};

  for (const name of SYNCED_TABLES) {
    const incoming = (parsed.tables?.[name] ?? []) as SyncedRecord[];
    if (!Array.isArray(incoming) || incoming.length === 0) continue;
    const table = syncedTable(name);
    const existing = await table.bulkGet(incoming.map((r) => r.id));
    const toWrite: SyncedRecord[] = [];
    incoming.forEach((rec, i) => {
      const current = existing[i];
      if (!current || (rec.updatedAt ?? 0) > current.updatedAt) toWrite.push(rec);
      else skipped += 1;
    });
    if (toWrite.length) await table.bulkPut(toWrite as never[]);
    restored += toWrite.length;
    perTable[name] = toWrite.length;
  }

  return { restored, skipped, perTable };
}
